import {
  ReactNode,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  ButtonHTMLAttributes,
  createContext,
  useContext,
} from "react";
import {
  FaSearch,
  FaTimes,
  FaInbox,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

export const ADMIN_ACCENT = "#2fa9bf";
export const EMPLOYEE_ACCENT = "#345952";

const PortalAccentContext = createContext(ADMIN_ACCENT);

export function usePortalAccent() {
  return useContext(PortalAccentContext);
}

export const adminTableHead =
  "py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]";

export const adminTableCell = "py-3.5 px-5 align-middle text-[#4A4A48]";

export function AdminPageHeader({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  const accent = usePortalAccent();
  return (
    <div
      className="rounded-2xl px-4 py-4 md:px-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      style={{ backgroundColor: accent }}
    >
      <div className="min-w-0">
        <h1 className="text-lg sm:text-xl font-bold text-white truncate">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-white/85 text-xs sm:text-sm mt-0.5">{subtitle}</p>
        ) : null}
      </div>
      {children ? (
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto min-w-0">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function AdminSearchInput({
  value,
  onChange,
  placeholder = "Search…",
  className = "w-full sm:w-[280px]",
  size = "md",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: "sm" | "md";
}) {
  const compact = size === "sm";
  return (
    <div className={`relative ${className}`}>
      <FaSearch
        className={`absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] pointer-events-none ${
          compact ? "text-[10px]" : "text-xs"
        }`}
      />
      <input
        type="text"
        placeholder={placeholder}
        className={`w-full rounded-xl bg-white border border-white text-[#1A1A18] placeholder:text-[#8A8A85] outline-none shadow-sm focus:ring-2 focus:ring-white/70 transition ${
          compact
            ? "pl-8 pr-8 py-2 text-xs"
            : "pl-9 pr-9 py-2.5"
        }`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A85] hover:text-[#4A4A48]"
        >
          <FaTimes className="text-xs" />
        </button>
      ) : null}
    </div>
  );
}

export function AdminSelect({
  value,
  onChange,
  children,
  className = "w-full sm:w-auto",
  size = "md",
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <select
      className={`rounded-xl bg-white border border-white text-[#1A1A18] px-3 outline-none shadow-sm focus:ring-2 focus:ring-white/70 cursor-pointer [&>option]:text-gray-800 ${
        size === "sm" ? "py-2 text-xs" : "py-2.5"
      } ${className}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </select>
  );
}

/** White pill for links/buttons sitting on the green AdminPageHeader. */
export const adminHeaderActionClass =
  "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-white px-3 py-2.5 text-xs font-semibold text-[#345952] shadow-sm transition hover:bg-[#E8F0EE]";

export function AdminHeaderButton({
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" {...props} className={`${adminHeaderActionClass} ${className}`}>
      {children}
    </button>
  );
}

export function AdminMetaRow({
  left,
  right,
}: {
  left?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-0.5">
      <div className="flex flex-wrap items-center gap-3 text-xs text-[#8A8A85]">
        {left}
      </div>
      {right ? <div className="flex items-center gap-2">{right}</div> : null}
    </div>
  );
}

export function AdminClearFilters({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[#2fa9bf] hover:text-[#2296aa] font-medium"
    >
      <FaTimes className="text-[10px]" />
      Clear filters
    </button>
  );
}

export function AdminSearchCapHint() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-0.5">
      Search shows up to 100 recent records
    </span>
  );
}

export function AdminPagination({
  page,
  totalPages,
  onPrev,
  onNext,
  disabled,
  tone = "default",
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  disabled?: boolean;
  tone?: "default" | "onAccent";
}) {
  const safeTotal = totalPages || 0;
  const onAccent = tone === "onAccent";
  const btnClass = onAccent
    ? "inline-flex items-center justify-center h-8 w-8 rounded-lg border-2 border-white bg-transparent text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
    : "inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition";
  const labelClass = onAccent
    ? "text-xs font-medium text-white tabular-nums min-w-[5.5rem] text-center"
    : "text-xs text-[#4A4A48] tabular-nums min-w-[5.5rem] text-center";

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={page <= 1 || disabled || safeTotal === 0}
        onClick={onPrev}
        className={btnClass}
        aria-label="Previous page"
      >
        <FaChevronLeft className="text-xs" />
      </button>
      <span className={labelClass}>
        Page {safeTotal === 0 ? 0 : page} of {safeTotal || 1}
      </span>
      <button
        type="button"
        disabled={page >= safeTotal || disabled || safeTotal === 0}
        onClick={onNext}
        className={btnClass}
        aria-label="Next page"
      >
        <FaChevronRight className="text-xs" />
      </button>
    </div>
  );
}

export function AdminTableCard({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`bg-white rounded-2xl border border-[#E8E8E4] shadow-sm ${
        className || "overflow-x-auto"
      }`}
    >
      {children}
    </div>
  );
}

export function AdminTableState({
  colSpan,
  variant,
  message,
  action,
}: {
  colSpan: number;
  variant: "loading" | "error" | "empty";
  message: string;
  action?: ReactNode;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className={`py-14 text-center ${
          variant === "error" ? "text-red-500" : "text-[#8A8A85]"
        }`}
      >
        <div className="inline-flex flex-col items-center gap-2">
          {variant === "loading" ? (
            <span
              className="h-8 w-8 rounded-full border-2 border-[#E8E8E4] border-t-[#2fa9bf] animate-spin"
              aria-hidden
            />
          ) : null}
          {variant === "empty" ? (
            <FaInbox className="text-2xl text-[#DDDDD8]" />
          ) : null}
          <span>{message}</span>
          {action}
        </div>
      </td>
    </tr>
  );
}

export function AdminPageShell({
  children,
  className = "",
  accent,
}: {
  children: ReactNode;
  className?: string;
  accent?: string;
}) {
  return (
    <PortalAccentContext.Provider value={accent || ADMIN_ACCENT}>
      <div
        className={`flex flex-col gap-3 w-full min-w-0 text-sm relative ${className}`}
      >
        {children}
      </div>
    </PortalAccentContext.Provider>
  );
}

export function AdminSoftInput({
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 ${className}`}
    />
  );
}

export function AdminSoftSelect({
  className = "",
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`rounded-xl border border-[#DDDDD8] bg-[#F8F8F6] px-3 py-2.5 text-sm text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20 cursor-pointer ${className}`}
    >
      {children}
    </select>
  );
}

export function AdminPrimaryButton({
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  const accent = usePortalAccent();
  return (
    <button
      type="button"
      {...props}
      style={{ backgroundColor: accent }}
      className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 ${className}`}
    >
      {children}
    </button>
  );
}

export function formatAdminDateTime(value: string | number | Date | null | undefined) {
  if (value == null || value === "") return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function relativeAdminTime(value: string | number | Date | null | undefined) {
  if (value == null || value === "") return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatAdminDateTime(value);
}
